import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useCompletedOrderDetail,
  useAcceptance,
  useAcceptanceTemplates,
  useCreateAcceptance,
  useUpdateAcceptance,
  useEmployeeSign,
  useUploadAcceptancePhotos,
} from '@saiens/shared/hooks';
import { useAuthStore } from '@saiens/shared/stores';
import { apiClient } from '@saiens/shared/api';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Check,
  Camera,
  ImagePlus,
  ChevronDown,
  ChevronUp,
  Package,
  Loader2,
} from 'lucide-react';
import type {
  AcceptanceCreateRequest,
  AcceptanceUpdateRequest,
  AcceptanceItem,
  AcceptanceItemStatus,
  AcceptanceSaleOrderLine,
} from '@saiens/shared/types';

const DEFAULT_COMPLETION_NOTE =
  '本公司已依設計圖面及客戶需求完成上述工程施作，施工品質經自我檢查確認符合標準。';

const INPUT_CLASS =
  'w-full rounded-xl border border-[#d2d2d7] bg-white px-3 py-2.5 text-base text-[#1d1d1f] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-[#f5f5f7] disabled:text-[#86868b]';

function AuthImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setObjectUrl(null);

    apiClient.get(src, { responseType: 'blob' })
      .then((res) => {
        if (!cancelled) {
          const url = URL.createObjectURL(res.data);
          urlRef.current = url;
          setObjectUrl(url);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
          setError(true);
        }
      });
    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [src]);

  if (loading) {
    return <div className={`bg-gray-200 animate-pulse ${className ?? ''}`} />;
  }
  if (error || !objectUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className ?? ''}`}>
        <span className="text-xs text-gray-400">載入失敗</span>
      </div>
    );
  }
  return <img src={objectUrl} alt={alt} className={className} />;
}

function generateNameSignature(name: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('無法建立簽名畫布');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 400, 160);
  ctx.fillStyle = '#000000';
  ctx.font = 'bold 48px "Microsoft JhengHei", "PingFang TC", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, 200, 80);
  return canvas.toDataURL('image/png');
}

export function AcceptanceFormPage() {
  const { orderId, id } = useParams<{ orderId?: string; id?: string }>();
  const navigate = useNavigate();
  const employee = useAuthStore((s) => s.employee);

  const isEdit = !!id;
  const numericOrderId = orderId ? parseInt(orderId, 10) : undefined;
  const numericId = id ? parseInt(id, 10) : undefined;

  // Fetch order detail for new acceptance
  const { data: orderDetail, isLoading: orderLoading } =
    useCompletedOrderDetail(numericOrderId);

  // Fetch existing acceptance for edit
  const { data: acceptance, isLoading: acceptanceLoading } =
    useAcceptance(numericId);

  // Fetch templates for new acceptance (create mode only)
  const { data: templates, isLoading: templatesLoading } =
    useAcceptanceTemplates(!isEdit);

  const createMutation = useCreateAcceptance();
  const updateMutation = useUpdateAcceptance();
  const employeeSignMutation = useEmployeeSign();
  const uploadPhotosMutation = useUploadAcceptancePhotos();

  // File input refs
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [completionDate, setCompletionDate] = useState('');
  const [completionNote, setCompletionNote] = useState(DEFAULT_COMPLETION_NOTE);
  const [photoMethodNote, setPhotoMethodNote] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([]);
  const [scores, setScores] = useState<Record<number, number>>({});
  const [itemStatuses, setItemStatuses] = useState<Record<number, AcceptanceItemStatus>>({});
  const [showOrderLines, setShowOrderLines] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<
    { id: number; name: string; url: string }[]
  >([]);
  const [paymentReceivedDate, setPaymentReceivedDate] = useState('');
  const [paymentAccountLast5, setPaymentAccountLast5] = useState('');
  const [paymentReceivedAmount, setPaymentReceivedAmount] = useState('');

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const targetOrderId = numericOrderId || acceptance?.sale_order_id;
      if (!targetOrderId) return;

      const base64Images: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) continue;
        if (file.size > 10 * 1024 * 1024) continue;
        try {
          const b64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              const idx = result.indexOf(',');
              resolve(idx >= 0 ? result.slice(idx + 1) : result);
            };
            reader.onerror = () => reject(new Error(`讀取失敗: ${file.name}`));
            reader.readAsDataURL(file);
          });
          base64Images.push(b64);
        } catch {
          // Skip files that fail to read
        }
      }

      if (base64Images.length === 0) return;

      const result = await uploadPhotosMutation.mutateAsync({
        saleOrderId: targetOrderId,
        images: base64Images,
      });

      // Add uploaded photos to local state + auto-select
      setUploadedPhotos((prev) => [...prev, ...result]);
      setSelectedPhotos((prev) => [...prev, ...result.map((p) => p.id)]);
    },
    [numericOrderId, acceptance?.sale_order_id, uploadPhotosMutation],
  );

  // Initialize form from existing acceptance
  useEffect(() => {
    if (acceptance) {
      setCompletionDate(acceptance.completion_date || '');
      setCompletionNote(acceptance.completion_note || DEFAULT_COMPLETION_NOTE);
      setPhotoMethodNote(acceptance.photo_method_note || '');
      setSelectedPhotos(acceptance.selected_photos?.map((p) => p.id) || []);
      const s: Record<number, number> = {};
      const statuses: Record<number, AcceptanceItemStatus> = {};
      acceptance.items?.forEach((item) => {
        s[item.id] = item.score;
        statuses[item.id] = item.status || 'done';
      });
      setScores(s);
      setItemStatuses(statuses);
      setPaymentReceivedDate(acceptance.payment_received_date || '');
      setPaymentAccountLast5(acceptance.payment_account_last5 || '');
      setPaymentReceivedAmount(
        acceptance.payment_received_amount ? String(acceptance.payment_received_amount) : ''
      );
    }
  }, [acceptance]);

  // Initialize completion date from order (prefer completion_date, fallback to construction_date)
  useEffect(() => {
    if (!completionDate && orderDetail) {
      const date = orderDetail.completion_date || orderDetail.construction_date;
      if (date) {
        setCompletionDate(date);
      }
    }
  }, [orderDetail, completionDate]);

  const handleScoreChange = useCallback(
    (itemId: number, score: number) => {
      setScores((prev) => ({ ...prev, [itemId]: score }));
    },
    [],
  );

  const handleStatusChange = useCallback(
    (itemId: number, status: AcceptanceItemStatus) => {
      setItemStatuses((prev) => ({ ...prev, [itemId]: status }));
      if (status === 'not_applicable') {
        setScores((prev) => ({ ...prev, [itemId]: 0 }));
      }
    },
    [],
  );

  const togglePhoto = useCallback((photoId: number) => {
    setSelectedPhotos((prev) =>
      prev.includes(photoId)
        ? prev.filter((id) => id !== photoId)
        : [...prev, photoId],
    );
  }, []);

  const saveAcceptance = async (): Promise<number | null> => {
    if (isEdit && numericId) {
      const data: AcceptanceUpdateRequest = {
        completion_date: completionDate,
        completion_note: completionNote,
        photo_method_note: photoMethodNote,
        selected_photo_ids: selectedPhotos,
        items: Object.entries(scores).map(([itemId, score]) => ({
          id: parseInt(itemId, 10),
          score: itemStatuses[Number(itemId)] === 'not_applicable' ? 0 : score,
          status: itemStatuses[Number(itemId)] || 'done',
        })),
        payment_received_date: paymentReceivedDate || undefined,
        payment_account_last5: paymentAccountLast5 || undefined,
        payment_received_amount: paymentReceivedAmount ? Number(paymentReceivedAmount) : undefined,
      };
      await updateMutation.mutateAsync({ id: numericId, data });
      return numericId;
    } else if (numericOrderId) {
      const data: AcceptanceCreateRequest = {
        sale_order_id: numericOrderId,
        construction_order_id: orderDetail?.construction_order_id || undefined,
        completion_date: completionDate,
        completion_note: completionNote,
        photo_method_note: photoMethodNote,
        selected_photo_ids: selectedPhotos,
        items: items.map((item) => ({
          sequence: item.sequence,
          score: itemStatuses[item.id] === 'not_applicable' ? 0 : scores[item.id] || 0,
          status: itemStatuses[item.id] || 'done',
        })),
        payment_received_date: paymentReceivedDate || undefined,
        payment_account_last5: paymentAccountLast5 || undefined,
        payment_received_amount: paymentReceivedAmount ? Number(paymentReceivedAmount) : undefined,
      };
      const result = await createMutation.mutateAsync(data);
      return result.id;
    }
    return null;
  };

  const handleSave = async () => {
    const savedId = await saveAcceptance();
    if (savedId && !isEdit) {
      navigate(`/acceptance/${savedId}`, { replace: true });
    }
  };

  // 業務單簽：簽名＝驗收完成（state 直接變 'signed'），簽完導向詳情頁送出
  const handleEmployeeSign = async () => {
    const name = employee?.name;
    if (!name) return;
    const hasApplicableUnscoredItem = items.some(
      (item) =>
        (itemStatuses[item.id] || item.status || 'done') === 'done'
        && (scores[item.id] ?? item.score) <= 0,
    );
    if (hasApplicableUnscoredItem) {
      window.alert('請先完成所有驗收項目評分');
      return;
    }
    if (
      !window.confirm(
        '簽名後即完成驗收，確定要簽名？\n\n完成後可在詳情頁送出驗收（自動建立應收憑單）。',
      )
    )
      return;

    const signatureData = generateNameSignature(name);

    try {
      const targetId = await saveAcceptance();
      if (!targetId) return;

      await employeeSignMutation.mutateAsync({
        id: targetId,
        signature: signatureData,
      });
      navigate(`/acceptance/${targetId}`, { replace: true });
    } catch {
      // Save or sign failed — mutations handle error display
    }
  };

  const isLoading = orderLoading || acceptanceLoading || templatesLoading;
  const items: AcceptanceItem[] = isEdit
    ? (acceptance?.items || [])
    : (templates || []).map((t, i) => ({
        id: -(i + 1),
        sequence: t.sequence,
        name: t.name,
        inspection_standard: t.inspection_standard,
        low_score_desc: t.low_score_desc,
        high_score_desc: t.high_score_desc,
        status: 'done',
        score: 0,
      }));
  const serverPhotos = orderDetail?.construction_photos || [];
  // Merge server photos with locally uploaded photos (deduplicate by id)
  const serverIds = new Set(serverPhotos.map((p: { id: number }) => p.id));
  const newUploaded = uploadedPhotos.filter((p) => !serverIds.has(p.id));
  const photos = [...serverPhotos, ...newUploaded];
  const orderLines: AcceptanceSaleOrderLine[] =
    acceptance?.order_lines || orderDetail?.order_lines || [];
  const projectName =
    acceptance?.project_name || orderDetail?.project_name || '';
  const partnerName =
    acceptance?.partner_name || orderDetail?.partner_name || '';
  const projectAddr =
    acceptance?.project_addr || orderDetail?.project_addr || '';
  const isSigned = acceptance?.state === 'signed';
  const hasEmployeeSignature = acceptance?.has_employee_signature || employeeSignMutation.isSuccess;

  // Goal-Gradient：完成度提示
  const applicableItems = items.filter(
    (item) => (itemStatuses[item.id] || item.status || 'done') === 'done',
  );
  const scoredCount = applicableItems.filter((item) => (scores[item.id] ?? item.score) > 0).length;
  const notApplicableCount = items.length - applicableItems.length;
  const hasUnscoredItems = scoredCount < applicableItems.length;
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const signButtonLabel = employeeSignMutation.isPending
    ? '簽名中...'
    : isSaving
      ? '儲存中...'
      : hasUnscoredItems
        ? `尚有 ${applicableItems.length - scoredCount} 項未評分`
        : '簽名並完成驗收';

  // 非 draft 一律導向詳情頁（2026-08-13 交叉審查修正：原本只擋 signed，
  // submitted/invoiced/done/cancelled 仍可進編輯頁但後端必回 INVALID_STATE）
  useEffect(() => {
    if (acceptance?.state && acceptance.state !== 'draft' && numericId) {
      navigate(`/acceptance/${numericId}`, { replace: true });
    }
  }, [acceptance?.state, numericId, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-40">
      <h1 className="text-xl font-semibold tracking-tight text-[#1d1d1f]">
        {isEdit ? '編輯驗收單' : '新增驗收單'}
      </h1>
      <p className="mb-6 mt-1 text-sm text-[#86868b]">
        填寫完成後簽名，即完成驗收
      </p>

      {/* 1. 客戶與基本資訊 */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <SectionTitle step={1} title="基本資訊" />
        <div className="space-y-2.5 text-sm">
          <InfoRow label="案名/案編" value={projectName} />
          <InfoRow label="客戶" value={partnerName} />
          <InfoRow label="施工地址" value={projectAddr} />
          <div className="pt-1">
            <label className="mb-1.5 block text-sm font-medium text-[#1d1d1f]">
              完工日期
            </label>
            <input
              type="date"
              value={completionDate}
              onChange={(e) => setCompletionDate(e.target.value)}
              className={INPUT_CLASS}
              disabled={isSigned}
            />
          </div>
        </div>
      </section>

      {/* 2. 訂單項目 (collapsible) */}
      {orderLines.length > 0 && (
        <section className="mb-4 rounded-2xl bg-[#f5f5f7]">
          <button
            type="button"
            onClick={() => setShowOrderLines((prev) => !prev)}
            className="flex min-h-[44px] w-full items-center justify-between p-4"
          >
            <h2 className="flex items-center gap-1.5 text-[15px] font-semibold text-[#1d1d1f]">
              <Package className="h-4 w-4 text-[#86868b]" />
              訂單項目 ({orderLines.length})
            </h2>
            {showOrderLines ? (
              <ChevronUp className="h-5 w-5 text-[#86868b]" />
            ) : (
              <ChevronDown className="h-5 w-5 text-[#86868b]" />
            )}
          </button>
          {showOrderLines && (
            <div className="px-4 pb-4">
              <div className="space-y-2">
                {orderLines.map((line) => (
                  <div
                    key={line.id}
                    className="flex items-center justify-between rounded-xl bg-white px-3 py-2 text-sm"
                  >
                    <span className="flex-1 text-[#1d1d1f]">{line.product_name}</span>
                    <div className="ml-3 shrink-0 text-right text-xs tabular-nums text-[#86868b]">
                      <span>{line.qty_delivered}/{line.product_uom_qty}</span>
                      {line.product_uom && (
                        <span className="ml-0.5">{line.product_uom}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-right text-xs text-[#86868b]">
                已交貨/訂購數量
              </p>
            </div>
          )}
        </section>
      )}

      {/* 3. 完工紀錄 */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <SectionTitle step={2} title="完工紀錄" />
        <textarea
          value={completionNote}
          onChange={(e) => setCompletionNote(e.target.value)}
          rows={4}
          className={INPUT_CLASS}
          disabled={isSigned}
        />
      </section>

      {/* 4. 驗收項目評分 */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <SectionTitle
          step={3}
          title="驗收評分"
          hint={
            `${scoredCount}/${applicableItems.length} 項`
            + `${notApplicableCount ? ` · 未施作 ${notApplicableCount}` : ''}`
          }
        />
        <div className="space-y-3">
          {items.map((item) => (
            <InspectionItemRow
              key={item.id}
              item={item}
              score={scores[item.id] ?? item.score}
              status={itemStatuses[item.id] || item.status || 'done'}
              onScoreChange={(s) => handleScoreChange(item.id, s)}
              onStatusChange={(status) => handleStatusChange(item.id, status)}
              disabled={isSigned}
            />
          ))}
        </div>
      </section>

      {/* 5. 完工照片（精選照＋建檔方式） */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <SectionTitle
          step={4}
          title="精選完工照"
          hint={`已選 ${selectedPhotos.length} 張`}
        />

        {/* Upload buttons */}
        {!isSigned && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploadPhotosMutation.isPending}
              className="flex min-h-[44px] items-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-sm font-medium text-primary ring-1 ring-inset ring-primary"
            >
              <Camera className="h-4 w-4" />
              拍照
            </button>
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploadPhotosMutation.isPending}
              className="flex min-h-[44px] items-center gap-1.5 rounded-full bg-white px-4 py-2.5 text-sm font-medium text-primary ring-1 ring-inset ring-primary"
            >
              <ImagePlus className="h-4 w-4" />
              選擇照片
            </button>
            {uploadPhotosMutation.isPending && (
              <div className="flex items-center gap-1 text-sm text-[#86868b]">
                <Loader2 className="h-4 w-4 animate-spin" />
                上傳中...
              </div>
            )}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                handleFileSelect(e.target.files);
                e.target.value = '';
              }}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                handleFileSelect(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
        )}

        {photos.length > 0 ? (
          <div className="grid grid-cols-3 gap-2">
            {photos.map(
              (photo: { id: number; name: string; url: string }) => (
                <button
                  key={photo.id}
                  onClick={() => togglePhoto(photo.id)}
                  className={`relative overflow-hidden rounded-xl border-2 ${
                    selectedPhotos.includes(photo.id)
                      ? 'border-primary'
                      : 'border-transparent'
                  }`}
                  disabled={isSigned}
                >
                  <AuthImage
                    src={photo.url}
                    alt={photo.name}
                    className="aspect-square w-full object-cover"
                  />
                  {selectedPhotos.includes(photo.id) && (
                    <div className="absolute right-1 top-1 rounded-full bg-primary p-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  )}
                </button>
              ),
            )}
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-[#86868b]">
            尚無照片，請使用上方按鈕拍照或上傳
          </p>
        )}

        <div className="mt-4">
          <label className="mb-1.5 block text-sm font-medium text-[#1d1d1f]">
            完工照片建檔方式
          </label>
          <textarea
            value={photoMethodNote}
            onChange={(e) => setPhotoMethodNote(e.target.value)}
            rows={2}
            className={INPUT_CLASS}
            placeholder="請說明完工照片的建檔方式..."
            disabled={isSigned}
          />
        </div>
      </section>

      {/* 6. 收款資訊（選填） */}
      <section className="mb-4 rounded-2xl bg-[#f5f5f7] p-4">
        <SectionTitle step={5} title="收款資訊" hint="選填" />
        <p className="mb-3 text-xs text-[#86868b]">
          如已收到款項，請填寫以下資訊供會計參考
        </p>
        <div className="space-y-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#1d1d1f]">收款日期</label>
            <input
              type="date"
              value={paymentReceivedDate}
              onChange={(e) => setPaymentReceivedDate(e.target.value)}
              disabled={isSigned}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#1d1d1f]">匯款帳號後五碼</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={paymentAccountLast5}
              onChange={(e) => setPaymentAccountLast5(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
              disabled={isSigned}
              placeholder="例：12345"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#1d1d1f]">收款金額</label>
            <input
              type="number"
              inputMode="numeric"
              value={paymentReceivedAmount}
              onChange={(e) => setPaymentReceivedAmount(e.target.value)}
              disabled={isSigned}
              placeholder="例：50000"
              className={INPUT_CLASS}
            />
          </div>
        </div>
      </section>

      {/* 底部固定操作列：一屏一決策 — 主 CTA 簽名並完成驗收 */}
      <div className="fixed bottom-16 left-0 right-0 border-t border-[#e8e8ed] bg-white/95 p-4 backdrop-blur safe-area-bottom">
        <p className="mb-2 text-center text-xs tabular-nums text-[#86868b]">
          評分 {scoredCount}/{applicableItems.length} 項
          {notApplicableCount ? ` · 未施作 ${notApplicableCount} 項` : ''} · 照片 {selectedPhotos.length} 張
        </p>
        {hasEmployeeSignature && !isSigned ? (
          <div className="rounded-full bg-[#1d1d1f] px-6 py-3 text-center text-[15px] font-medium text-white">
            已簽名，驗收完成
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving || isSigned}
              className="min-h-[44px] shrink-0 px-4 text-[15px] font-medium text-primary disabled:text-[#86868b]"
            >
              {isSaving ? '儲存中...' : '儲存草稿'}
            </button>
            <button
              onClick={handleEmployeeSign}
              disabled={employeeSignMutation.isPending || isSaving || isSigned || hasUnscoredItems}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-[15px] font-medium text-white disabled:opacity-50"
            >
              <Check className="h-4 w-4" />
              {signButtonLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionTitle({
  step,
  title,
  hint,
}: {
  step: number;
  title: string;
  hint?: string;
}) {
  return (
    <div className="mb-3 flex items-baseline justify-between">
      <h2 className="text-[15px] font-semibold text-[#1d1d1f]">
        <span className="mr-1.5 text-[#86868b]">{step}.</span>
        {title}
      </h2>
      {hint && <span className="text-xs tabular-nums text-[#86868b]">{hint}</span>}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex">
      <span className="w-24 shrink-0 text-[#86868b]">{label}</span>
      <span className="text-[#1d1d1f]">{value || '---'}</span>
    </div>
  );
}

function InspectionItemRow({
  item,
  score,
  status,
  onScoreChange,
  onStatusChange,
  disabled,
}: {
  item: AcceptanceItem;
  score: number;
  status: AcceptanceItemStatus;
  onScoreChange: (score: number) => void;
  onStatusChange: (status: AcceptanceItemStatus) => void;
  disabled: boolean;
}) {
  const isNotApplicable = status === 'not_applicable';
  return (
    <div className="rounded-xl bg-white p-3">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="mb-0.5 text-sm font-medium text-[#1d1d1f]">{item.name}</div>
          {item.inspection_standard && (
            <div className="text-xs text-[#86868b]">
              {item.inspection_standard}
            </div>
          )}
        </div>
        <label className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-[#1d1d1f]">
          <input
            type="checkbox"
            checked={isNotApplicable}
            onChange={(e) => onStatusChange(e.target.checked ? 'not_applicable' : 'done')}
            disabled={disabled}
            className="h-4 w-4 accent-primary"
          />
          未施作
        </label>
      </div>
      <div className={`flex justify-center gap-2 ${isNotApplicable ? 'opacity-40' : ''}`}>
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            key={s}
            onClick={() => onScoreChange(s)}
            disabled={disabled || isNotApplicable}
            className={`h-10 w-10 rounded-full text-sm font-medium tabular-nums transition-colors ${
              score === s
                ? 'bg-primary text-white'
                : 'bg-[#f5f5f7] text-[#6e6e73] hover:bg-[#e8e8ed]'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      {isNotApplicable ? (
        <div className="mt-1.5 text-center text-xs text-[#86868b]">此項不計分</div>
      ) : (
        <div className="mt-1.5 flex justify-between text-xs text-[#86868b]">
          <span>{item.low_score_desc}</span>
          <span>{item.high_score_desc}</span>
        </div>
      )}
    </div>
  );
}
